/* CycleAwareCard — iter424. Менструален цикъл tracker + phase-specific препоръки. */
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Moon, Heart, Leaf, Hand, Sparkles, Calendar, Loader2, Activity } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import PhaseRemindersBell from "@/components/PhaseRemindersBell";

export default function CycleAwareCard() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [hasData, setHasData] = useState(false);
  const [phase, setPhase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastPeriodStart, setLastPeriodStart] = useState(new Date().toISOString().slice(0, 10));
  const [cycleLength, setCycleLength] = useState(28);
  const [periodLength, setPeriodLength] = useState(5);
  const [flow, setFlow] = useState("normal");
  const [ironNote, setIronNote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  const fetchCurrent = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/cycle-aware/current`);
      if (r.data?.has_data) {
        setHasData(true);
        setPhase(r.data.current_phase);
        if (r.data.last_period_start) setLastPeriodStart(r.data.last_period_start);
        if (r.data.cycle_length_days) setCycleLength(r.data.cycle_length_days);
        if (r.data.flow_intensity) setFlow(r.data.flow_intensity);
        setIronNote(r.data.iron_note_bg || null);
      } else {
        setHasData(false);
      }
    } catch (_e) {
      setHasData(false);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchCurrent(); }, [fetchCurrent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/cycle-aware/log`, {
        last_period_start: lastPeriodStart,
        cycle_length_days: Number(cycleLength) || 28,
        period_length_days: Number(periodLength) || 5,
        flow_intensity: flow,
      });
      setPhase(r.data.current_phase);
      setIronNote(r.data.iron_note_bg || null);
      setHasData(true);
      setEditing(false);
      toast.success(bg ? "Цикълът е записан ✓" : "Cycle saved ✓");
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally { setSaving(false); }
  };

  // Phase color map
  const colorClasses = {
    rose:    isDark ? "from-rose-500 to-pink-500" : "from-rose-500 to-pink-500",
    emerald: isDark ? "from-emerald-500 to-teal-500" : "from-emerald-500 to-teal-500",
    amber:   isDark ? "from-amber-500 to-orange-500" : "from-amber-500 to-orange-500",
    violet:  isDark ? "from-violet-500 to-fuchsia-500" : "from-violet-500 to-fuchsia-500",
  };
  const phaseColor = colorClasses[phase?.phase_color] || colorClasses.violet;

  return (
    <Card className={`border ${isDark ? "border-pink-500/30 bg-gradient-to-br from-pink-500/5 to-rose-500/5" : "border-pink-200 bg-gradient-to-br from-pink-50/40 to-rose-50/30"}`}
          data-testid="cycle-aware-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-gradient-to-br from-pink-500 to-rose-500 text-white">
            <Moon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "Cycle-Aware хранене" : "Cycle-Aware Nutrition"}
            </h3>
            <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {bg ? "Фазно-чувствителни препоръки за хранене и движение." : "Phase-sensitive nutrition + movement guidance."}
            </p>
          </div>
          <PhaseRemindersBell />
        </div>

        {loading ? (
          <div className={`p-4 text-center ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            <Loader2 className="inline w-4 h-4 animate-spin mr-1" /> {bg ? "Зарежда…" : "Loading…"}
          </div>
        ) : (!hasData || editing) ? (
          <div className={`p-3 rounded-xl border space-y-2 ${isDark ? "border-pink-500/30 bg-pink-500/5" : "border-pink-200 bg-pink-50/40"}`}>
            <p className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "Логнете началото на последния цикъл" : "Log last period start"}
            </p>
            <div className="space-y-1.5">
              <label className={`text-[10.5px] block ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                {bg ? "Дата на началото (ден 1)" : "First day of period"}
              </label>
              <Input type="date" value={lastPeriodStart} onChange={(e) => setLastPeriodStart(e.target.value)}
                     data-testid="cycle-date-input"
                     className={`text-xs ${isDark ? "bg-slate-800 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className={`text-[10.5px] block ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  {bg ? "Дължина на цикъла (дни)" : "Cycle length"}
                </label>
                <Input type="number" min={21} max={40} value={cycleLength}
                       onChange={(e) => setCycleLength(e.target.value)}
                       data-testid="cycle-length-input"
                       className={`text-xs ${isDark ? "bg-slate-800 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`} />
              </div>
              <div className="space-y-1">
                <label className={`text-[10.5px] block ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  {bg ? "Дни менструация" : "Period days"}
                </label>
                <Input type="number" min={2} max={8} value={periodLength}
                       onChange={(e) => setPeriodLength(e.target.value)}
                       data-testid="period-length-input"
                       className={`text-xs ${isDark ? "bg-slate-800 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`} />
              </div>
            </div>
            <div className="space-y-1">
              <label className={`text-[10.5px] block ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                {bg ? "Колко обилно кървите" : "Flow intensity"}
              </label>
              <div className="flex gap-1.5">
                {[
                  { k: "light", bg: "Слабо", en: "Light" },
                  { k: "normal", bg: "Нормално", en: "Normal" },
                  { k: "heavy", bg: "Обилно", en: "Heavy" },
                ].map((o) => (
                  <button key={o.k} type="button" onClick={() => setFlow(o.k)}
                    className={`px-2 py-1 rounded-lg text-[11px] font-semibold border transition-colors ${
                      flow === o.k
                        ? "bg-rose-500 text-white border-rose-500"
                        : isDark ? "border-slate-700 text-slate-300" : "border-slate-300 text-slate-700"}`}
                    data-testid={`cycle-flow-${o.k}`}>
                    {bg ? o.bg : o.en}
                  </button>
                ))}
              </div>
              {flow === "heavy" && (
                <p className={`text-[10px] ${isDark ? "text-amber-300" : "text-amber-700"}`}
                  data-testid="cycle-flow-iron-hint">
                  {bg ? "Обилната менструация е водещата загуба на желязо — хематологичният близнак ще го отчете."
                      : "Heavy flow is the leading cause of iron loss."}
                </p>
              )}
            </div>
            <Button onClick={handleSave} disabled={saving}
                    className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white"
                    data-testid="cycle-save-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Calendar className="w-3.5 h-3.5 mr-1" />}
              {bg ? "Запази" : "Save"}
            </Button>
          </div>
        ) : phase && (
          <>
            {ironNote && (
              <div className={`p-2 rounded-lg text-[11px] ${isDark ? "bg-amber-500/10 text-amber-200" : "bg-amber-50 text-amber-800"}`}
                data-testid="cycle-iron-note">
                {ironNote}
              </div>
            )}
            {/* Phase header */}
            <div className={`p-3 rounded-xl bg-gradient-to-br ${phaseColor} text-white`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-wider opacity-90">
                    {bg ? "Текуща фаза" : "Current phase"}
                  </p>
                  <p className="text-lg font-bold flex items-center gap-1.5">
                    <span className="text-xl">{phase.phase_icon}</span> {phase.phase_name_bg}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] opacity-80">{bg ? "Ден" : "Day"}</p>
                  <p className="text-xl font-bold">{phase.cycle_day}<span className="text-xs opacity-70">/{phase.cycle_length}</span></p>
                </div>
              </div>
              <p className="text-[11px] mt-1.5 opacity-95 leading-relaxed">{phase.description_bg}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full">
                  {bg ? `До следваща менструация: ${phase.days_to_next_period} дни` : `Next: ${phase.days_to_next_period}d`}
                </span>
                {phase.dil_adjustment !== 0 && (
                  <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full">
                    DIL Δ {phase.dil_adjustment > 0 ? "+" : ""}{phase.dil_adjustment}g
                  </span>
                )}
              </div>
            </div>

            {/* NIS target advice */}
            {phase.nis_target_bg && (
              <div className={`p-2 rounded-lg flex items-start gap-2 ${isDark ? "bg-violet-500/10" : "bg-violet-50"}`}>
                <Activity className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isDark ? "text-violet-300" : "text-violet-700"}`} />
                <p className={`text-[10.5px] leading-relaxed ${isDark ? "text-violet-100" : "text-violet-900"}`}>
                  {phase.nis_target_bg}
                </p>
              </div>
            )}

            {/* Favor + limit grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <p className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                  <Leaf className="w-3 h-3" /> {bg ? "Дайте предпочитание" : "Favor"}
                </p>
                <ul className={`text-[10.5px] space-y-0.5 ${isDark ? "text-emerald-100" : "text-emerald-900"}`} data-testid="cycle-favor-list">
                  {(phase.favor_foods_bg || []).slice(0, 6).map((f, i) => <li key={i}>• {f}</li>)}
                </ul>
              </div>
              <div>
                <p className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                  <Hand className="w-3 h-3" /> {bg ? "Ограничете" : "Limit"}
                </p>
                <ul className={`text-[10.5px] space-y-0.5 ${isDark ? "text-amber-100" : "text-amber-900"}`} data-testid="cycle-limit-list">
                  {(phase.limit_bg || []).slice(0, 4).map((f, i) => <li key={i}>• {f}</li>)}
                </ul>
              </div>
            </div>

            {/* Mind-body practices */}
            {Array.isArray(phase.mindbody_bg) && phase.mindbody_bg.length > 0 && (
              <div className={`p-2.5 rounded-xl border ${isDark ? "border-fuchsia-500/30 bg-fuchsia-500/5" : "border-fuchsia-200 bg-fuchsia-50/40"}`}>
                <p className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1.5 ${isDark ? "text-fuchsia-300" : "text-fuchsia-700"}`}>
                  <Sparkles className="w-3 h-3" /> {bg ? "Тяло-ум практики за фазата" : "Mind-body for this phase"}
                </p>
                <div className="space-y-1">
                  {phase.mindbody_bg.map((p, i) => (
                    <div key={i} className="text-[10.5px] leading-relaxed">
                      <span className={`font-bold ${isDark ? "text-fuchsia-100" : "text-fuchsia-900"}`}>{p.name}</span>
                      <span className={`italic block ${isDark ? "text-slate-400" : "text-slate-600"}`}>{p.rationale}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Edit */}
            <Button onClick={() => setEditing(true)} variant="ghost"
                    data-testid="cycle-edit-btn"
                    className={`w-full text-[11px] ${isDark ? "text-slate-300 hover:bg-slate-800" : "text-slate-700 hover:bg-slate-100"}`}>
              <Calendar className="w-3 h-3 mr-1" /> {bg ? "Обнови датите" : "Update dates"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
